// src/components/events/EventRegistration.tsx
//
// Two-column event registration component.
// Once a registrant selects their region, a province dropdown
// appears and is populated from GET /api/events/regions/:regionId/provinces.

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Wifi,
  Users,
  User,
  Mail,
  Phone,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  ExternalLink,
  Shield,
  ArrowLeft,
  Building2,
} from "lucide-react";
import {
  useRegisterForEventMutation,
  useGetEventRegionsQuery,
  useGetEventProvincesQuery,
} from "../../app/api/eventApi";
import type { EventData, Speaker } from "../../types/eventTypes";
import { staticEvent } from "../../data/staticEvent";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export interface RegistrationResult {
  confirmationCode: string;
  status: string;
  eventName: string;
  eventSlug: string;
}

export interface EventRegistrationProps {
  event?: EventData;
  onSuccess?: (result: RegistrationResult) => void;
  onCancel?: () => void;
  asModal?: boolean;
  className?: string;
}

/* ─────────────────────────────────────────────────────────────────
   DATE HELPERS
───────────────────────────────────────────────────────────────── */

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const fmtTime = (d: string) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

const PLACEHOLDER = "/src/assets/images/adeolu.png";

/* ─────────────────────────────────────────────────────────────────
   SPEAKER ROW
───────────────────────────────────────────────────────────────── */

const SpeakerRow: React.FC<{ speaker: Speaker }> = ({ speaker }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <div className="shrink-0 w-9 h-9 rounded-full overflow-hidden border-2 border-green-500/40">
        <img
          src={err || !speaker.photoUrl ? PLACEHOLDER : speaker.photoUrl}
          alt={speaker.name}
          onError={() => setErr(true)}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-white leading-tight truncate">
          {speaker.name}
        </p>
        {speaker.topic && (
          <p className="text-[10px] font-black tracking-widest uppercase text-green-400 truncate">
            {speaker.topic}
          </p>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   EVENT INFO PANEL
───────────────────────────────────────────────────────────────── */

const InfoPanel: React.FC<{ event: EventData }> = ({ event }) => {
  const speakers = useMemo(
    () =>
      [...(event.speakers ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [event.speakers],
  );

  return (
    <div className="relative flex flex-col gap-6 overflow-hidden bg-green-900 p-7 md:p-10">
      {event.bannerImageUrl && (
        <>
          <img
            src={event.bannerImageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center opacity-10 pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/95 to-green-950/90 pointer-events-none" />
        </>
      )}

      <div className="relative z-10 flex flex-col gap-5 h-full">
        {event.tagline && (
          <div className="self-start inline-flex items-center gap-2 border border-green-400/40 rounded-full px-3 py-1.5 text-green-400 text-[10px] font-black tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {event.tagline}
          </div>
        )}

        <div>
          <h2 className="text-xl md:text-2xl font-black text-white leading-tight mb-2">
            {event.name}
          </h2>
          {event.description && (
            <p className="text-sm text-white/55 leading-relaxed line-clamp-3">
              {event.description}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5 text-sm text-white/75">
            <Calendar size={14} className="text-green-400 shrink-0" />
            <span className="font-medium">{fmtDate(event.eventDate)}</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-white/75">
            <Clock size={14} className="text-green-400 shrink-0" />
            <span>
              {fmtTime(event.eventDate)}
              {event.eventEndDate && ` – ${fmtTime(event.eventEndDate)}`}
              {event.timezone && (
                <span className="text-white/40"> {event.timezone}</span>
              )}
            </span>
          </div>

          {event.isVirtual ? (
            <div className="flex items-center gap-2.5 text-sm text-white/75">
              <Wifi size={14} className="text-green-400 shrink-0" />
              <span>
                Live Online
                {event.streamUrl && (
                  <a
                    href={event.streamUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 text-[11px] font-semibold text-green-400 hover:text-green-300 underline underline-offset-2"
                  >
                    Watch link →
                  </a>
                )}
              </span>
            </div>
          ) : event.venue ? (
            <div className="flex items-start gap-2.5 text-sm text-white/75">
              <MapPin size={14} className="text-green-400 shrink-0 mt-0.5" />
              <span>
                {event.venue}
                {event.venueAddress && (
                  <span className="block text-xs text-white/40">
                    {event.venueAddress}
                  </span>
                )}
              </span>
            </div>
          ) : null}

          {(event.registrationCount ?? 0) > 0 && (
            <div className="flex items-center gap-2.5 text-sm text-white/75">
              <Users size={14} className="text-green-400 shrink-0" />
              <span>
                <strong className="text-green-400">
                  {event.registrationCount!.toLocaleString()}
                </strong>{" "}
                registered
              </span>
            </div>
          )}
        </div>

        {speakers.length > 0 && (
          <div className="mt-auto pt-5 border-t border-white/10">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/35 mb-3">
              Speakers
            </p>
            <div className="flex flex-col gap-3">
              {speakers.slice(0, 4).map((s, i) => (
                <SpeakerRow key={s._id ?? i} speaker={s} />
              ))}
              {speakers.length > 4 && (
                <p className="text-[11px] text-white/30">
                  +{speakers.length - 4} more speakers
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   FIELD WRAPPER
───────────────────────────────────────────────────────────────── */

const Field: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}> = ({ label, required, error, children, hint }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-semibold text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-[10px] text-gray-400">{hint}</p>}
    {error && (
      <p className="text-[11px] text-red-600 flex items-center gap-1">
        <AlertCircle size={10} className="shrink-0" /> {error}
      </p>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   SUCCESS SCREEN
───────────────────────────────────────────────────────────────── */

const SuccessScreen: React.FC<{
  result: RegistrationResult;
  onClose?: () => void;
}> = ({ result, onClose }) => (
  <div className="text-center py-10 px-4">
    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
      <CheckCircle2 size={32} className="text-green-600" />
    </div>
    <h3 className="text-xl font-black text-gray-900 mb-2">
      You're registered! 🎉
    </h3>
    <p className="text-sm text-gray-500 leading-relaxed mb-6">
      You've successfully registered for{" "}
      <strong className="text-gray-800">{result.eventName}</strong>.
      {result.status === "waitlisted" && (
        <span className="block mt-1 text-amber-600 font-medium text-xs">
          The event is full — you've been added to the waitlist.
        </span>
      )}
    </p>

    <div className="inline-flex flex-col items-center gap-1 px-8 py-4 rounded-2xl bg-green-50 border border-green-200 mb-5">
      <p className="text-[10px] font-black uppercase tracking-widest text-green-600">
        Confirmation code
      </p>
      <p className="text-2xl font-black tracking-wider text-gray-900 font-mono">
        {result.confirmationCode}
      </p>
    </div>

    <p className="text-[11px] text-gray-400 mb-5">
      Save this code — you may need it at the event.
    </p>

    <div className="flex items-center justify-center gap-4">
      <Link
        to="/event"
        className="text-sm font-semibold text-green-600 hover:text-green-700 transition-colors no-underline"
      >
        ← Back to event
      </Link>
      {onClose && (
        <button
          onClick={onClose}
          className="text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors"
        >
          Close
        </button>
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const EventRegistration: React.FC<EventRegistrationProps> = ({
  event: eventProp,
  onSuccess,
  onCancel,
  asModal = false,
  className = "",
}) => {
  const event = eventProp ?? staticEvent;

  const { data: regionsRes, isLoading: regionsLoading } =
    useGetEventRegionsQuery();
  const regionOptions = useMemo(() => regionsRes?.data ?? [], [regionsRes]);

  const [registerForEvent, { isLoading: submitting }] =
    useRegisterForEventMutation();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    regionId: "",
    provinceId: "",
    privacyConsent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<RegistrationResult | null>(null);

  // Fetch provinces when a region is selected
  const { data: provincesRes, isFetching: provincesLoading } =
    useGetEventProvincesQuery(form.regionId, {
      skip: !form.regionId,
    });
  const provinceOptions = useMemo(
    () => provincesRes?.data ?? [],
    [provincesRes],
  );

  const hasProvinceOptions = !!form.regionId && provinceOptions.length > 0;

  const set = (field: string, value: string | boolean) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "regionId") next.provinceId = "";
      return next;
    });
  };

  const validate = () => {
    const e: Record<string, string> = {};

    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";

    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Enter a valid email address";
    }

    if (!form.regionId) e.regionId = "Please select your region";

    if (
      form.regionId &&
      !provincesLoading &&
      provinceOptions.length > 0 &&
      !form.provinceId
    ) {
      e.provinceId = "Please select your province";
    }

    if (!form.privacyConsent) e.privacy = "You must accept the privacy policy";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    setErrors({});
    try {
      const res = await registerForEvent({
        slug: event.slug,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phoneNo: form.phoneNo.trim() || undefined,
        regionId: form.regionId,
        provinceId: form.provinceId || undefined,
        eventName: event.name,
        privacyConsent: true,
      }).unwrap();

      setSuccess(res.data);
      onSuccess?.(res.data);
    } catch (err: any) {
      const msg =
        err?.data?.error ??
        err?.data?.details?.[0] ??
        "Registration failed. Please try again.";
      setErrors((prev) => ({ ...prev, submit: msg }));
    }
  };

  const inp = (hasErr: boolean) =>
    `w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-all
     placeholder-gray-400 bg-white text-gray-900
     ${
       hasErr
         ? "border-red-300 focus:ring-2 focus:ring-red-200"
         : "border-gray-200 focus:ring-2 focus:ring-green-200 focus:border-green-400"
     }`;

  const formPanel = (
    <div className="flex flex-col h-full">
      {success ? (
        <SuccessScreen result={success} onClose={onCancel} />
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div className="flex items-center justify-between mb-5">
            <Link
              to="/event"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-600 hover:text-green-700 transition-colors no-underline"
            >
              <ArrowLeft size={13} />
              Back to event
            </Link>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="mb-5">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 mb-3">
              <User size={10} />
              Register
            </div>
            <p className="text-xs text-gray-500">
              Fields marked <span className="text-red-500 font-bold">*</span>{" "}
              are required.
            </p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First name" required error={errors.firstName}>
                <div className="relative">
                  <User
                    size={13}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder="Adaeze"
                    autoComplete="given-name"
                    className={`${inp(!!errors.firstName)} pl-9`}
                  />
                </div>
              </Field>

              <Field label="Last name" required error={errors.lastName}>
                <div className="relative">
                  <User
                    size={13}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder="Adekunle"
                    autoComplete="family-name"
                    className={`${inp(!!errors.lastName)} pl-9`}
                  />
                </div>
              </Field>
            </div>

            <Field label="Email address" required error={errors.email}>
              <div className="relative">
                <Mail
                  size={13}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="adaeze@example.com"
                  autoComplete="email"
                  className={`${inp(!!errors.email)} pl-9`}
                />
              </div>
            </Field>

            <Field label="Phone number">
              <div className="relative">
                <Phone
                  size={13}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="tel"
                  value={form.phoneNo}
                  onChange={(e) => set("phoneNo", e.target.value)}
                  placeholder="+234 123 9000"
                  autoComplete="tel"
                  className={`${inp(false)} pl-9`}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">
                This will be used for contacting you
              </p>
            </Field>

            <Field label="Region" required error={errors.regionId}>
              <div className="relative">
                <MapPin
                  size={13}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10"
                />
                <select
                  value={form.regionId}
                  onChange={(e) => set("regionId", e.target.value)}
                  disabled={regionsLoading}
                  className={`${inp(!!errors.regionId)} pl-9 appearance-none`}
                >
                  <option value="">
                    {regionsLoading
                      ? "Loading regions…"
                      : "Select your region…"}
                  </option>
                  {regionOptions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </Field>

            {form.regionId && (
              <Field
                label="Province"
                required={hasProvinceOptions && !provincesLoading}
                error={errors.provinceId}
                hint={
                  provinceOptions.length === 0 && !provincesLoading
                    ? "No provinces available for this region yet."
                    : undefined
                }
              >
                <div className="relative">
                  <Building2
                    size={13}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10"
                  />
                  <select
                    value={form.provinceId}
                    onChange={(e) => set("provinceId", e.target.value)}
                    disabled={provincesLoading || provinceOptions.length === 0}
                    className={`${inp(!!errors.provinceId)} pl-9 appearance-none`}
                  >
                    <option value="">
                      {provincesLoading
                        ? "Loading provinces…"
                        : provinceOptions.length === 0
                          ? "No provinces available"
                          : "Select your province…"}
                    </option>
                    {provinceOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>

                  {provincesLoading && (
                    <Loader2
                      size={12}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-gray-400 pointer-events-none"
                    />
                  )}
                </div>
              </Field>
            )}

            <div
              className={`rounded-xl border p-4 transition-all ${
                errors.privacy
                  ? "border-red-200 bg-red-50"
                  : form.privacyConsent
                    ? "border-green-200 bg-green-50"
                    : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={form.privacyConsent}
                  onClick={() => set("privacyConsent", !form.privacyConsent)}
                  className={`shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                    ${
                      form.privacyConsent
                        ? "bg-green-600 border-green-600"
                        : "bg-white border-gray-300 hover:border-green-400"
                    }`}
                >
                  {form.privacyConsent && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>

                <div className="flex-1">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    I agree to the{" "}
                    <a
                      href={event.privacyPolicyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline underline-offset-2 text-green-600 hover:text-green-700 inline-flex items-center gap-0.5"
                    >
                      Privacy Policy <ExternalLink size={9} />
                    </a>{" "}
                    and{" "}
                    <a
                      href={event.dataProtectionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline underline-offset-2 text-green-600 hover:text-green-700 inline-flex items-center gap-0.5"
                    >
                      Data Protection Policy <ExternalLink size={9} />
                    </a>
                    . My data will be used to manage my registration.
                    <span className="text-red-500 ml-0.5">*</span>
                  </p>

                  {errors.privacy && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={10} /> {errors.privacy}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-gray-400 leading-relaxed">
              <Shield size={11} className="shrink-0 mt-0.5" />
              <span>
                Your data is stored securely and will not be shared without your
                consent.
              </span>
            </div>

            {errors.submit && (
              <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <AlertCircle size={13} className="shrink-0" /> {errors.submit}
              </div>
            )}

            <button
              type="submit"
              disabled={
                submitting ||
                regionsLoading ||
                (!!form.regionId && provincesLoading)
              }
              className="w-full py-3 mt-1 text-sm font-black rounded-xl text-white bg-green-600 hover:bg-green-500 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 shadow-sm shadow-green-900/30"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Registering…
                </>
              ) : (
                "Complete Registration"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const twoCol = (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 overflow-hidden rounded-3xl shadow-2xl max-w-[900px] w-full ${className}`}
    >
      <InfoPanel event={event} />
      <div className="bg-white overflow-y-auto p-7 md:p-10">{formPanel}</div>
    </div>
  );

  if (asModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-md">
        {twoCol}
      </div>
    );
  }

  return <div className="w-full max-w-[900px] mx-auto py-16">{twoCol}</div>;
};

export default EventRegistration;
